import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Users, Gift, CalendarCheck, Link2, UserPlus, Sparkles } from "lucide-react";
import { api, formatApiError, PUBLIC_ORIGIN } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Page, PageHead } from "@/components/hub/Common";
import { ReferralLeaderboard } from "@/components/referral/ReferralLeaderboard";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="cx-card rounded-2xl p-5">
    <div className="flex items-center gap-2 text-xs text-slate-400"><Icon className="w-4 h-4" style={{ color }} />{label}</div>
    <div className="font-display text-3xl font-bold text-white mt-2" data-testid={testid}>{value}</div>
  </div>
);

const Step = ({ n, icon: Icon, text }) => (
  <div className="flex gap-3 items-start">
    <div className="w-9 h-9 shrink-0 rounded-xl grid place-items-center bg-[#2f7bff]/15 border border-[#2f7bff]/40 text-[#3ad7ff] font-mono text-sm">{n}</div>
    <div className="text-sm text-slate-300 pt-2 flex gap-2"><Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />{text}</div>
  </div>
);

const ShareButtons = ({ link, text, t }) => {
  const enc = encodeURIComponent;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success(t("ref.copied")); } catch { toast.error(t("tr.copy_failed")); }
  };
  const native = () => (navigator.share ? navigator.share({ title: "CLIP-EX", text, url: link }).catch(() => {}) : copy());
  const nets = [["whatsapp", "WhatsApp", `https://wa.me/?text=${enc(`${text} ${link}`)}`], ["x", "X", `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(link)}`],
    ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}`]];
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      <button onClick={copy} data-testid="referral-copy-btn" className="cx-btn rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2"><Copy className="w-3.5 h-3.5" />{t("ref.copy")}</button>
      <button onClick={native} data-testid="referral-share-btn" className="cx-metal rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2"><Share2 className="w-3.5 h-3.5" />{t("ref.share")}</button>
      {nets.map(([id, label, href]) => <a key={id} href={href} target="_blank" rel="noreferrer" data-testid={`referral-share-${id}`} className="cx-metal rounded-full px-4 py-2.5 text-xs">{label}</a>)}
    </div>
  );
};

export default function Referral() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/referral").then((r) => setD(r.data)).catch((e) => { setD(false); toast.error(formatApiError(e)); }); }, []);
  const link = d ? `${PUBLIC_ORIGIN}${d.path}` : "";
  const text = d ? t("ref.share_text").replace("{n}", d.reward_friend) : "";
  return (
    <Page>
      <div data-testid="referral-page">
        <PageHead label={t("ref.label")} t1={t("ref.t1")} t2={t("ref.t2")} sub={d ? t("ref.sub").replace("{a}", d.reward_referrer).replace("{b}", d.reward_friend) : t("ref.sub").replace("{a}", 3).replace("{b}", 3)} />
        {d === null && <div className="cx-skel h-40 rounded-2xl mt-10" />}
        {d && (
          <>
            <div className="mt-10 cx-card rounded-2xl p-6 border border-[#22e3a1]/30 relative overflow-hidden">
              <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#22e3a1]/10 blur-3xl" />
              <div className="text-xs text-slate-400 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#22e3a1]" />{t("ref.link")}</div>
              <div className="mt-2 font-mono text-sm sm:text-lg text-white break-all" data-testid="referral-link">{link}</div>
              <div className="text-xs text-slate-500 mt-1">{t("ref.code")} : <span className="font-mono text-[#ffc94d]" data-testid="referral-code">{d.code}</span></div>
              <ShareButtons link={link} text={text} t={t} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
              <Stat icon={Users} label={t("ref.friends")} value={d.friends} color="#3ad7ff" testid="referral-friends" />
              <Stat icon={Gift} label={t("ref.earned")} value={d.earned} color="#22e3a1" testid="referral-earned" />
              <Stat icon={CalendarCheck} label={t("ref.month")} value={`${d.month_rewarded}/${d.cap}`} color="#ffc94d" testid="referral-month" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6 mt-8">
              <div className="cx-card rounded-2xl p-6 space-y-4">
                <h2 className="text-base md:text-lg font-semibold text-white">{t("ref.how")}</h2>
                <Step n={1} icon={Share2} text={t("ref.step1")} />
                <Step n={2} icon={UserPlus} text={t("ref.step2")} />
                <Step n={3} icon={Sparkles} text={t("ref.step3")} />
                <p className="text-[11px] text-slate-500 pt-2">{t("ref.rules").replace("{cap}", d.cap)}</p>
              </div>
              <div className="cx-card rounded-2xl p-6">
                <h2 className="text-base md:text-lg font-semibold text-white">{t("ref.list")}</h2>
                <div className="mt-4 space-y-2" data-testid="referral-list">
                  {!d.rows.length && <p className="text-sm text-slate-500">{t("ref.none")}</p>}
                  {d.rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-black/30 px-4 py-2.5 text-sm" data-testid={`referral-row-${i}`}>
                      <span className="text-white">{r.name}</span>
                      <span className={r.status === "rewarded" ? "text-[#22e3a1] text-xs" : "text-slate-500 text-xs"}>{t(`ref.st_${r.status}`).replace("{n}", d.reward_referrer)}</span>
                      <span className="text-slate-500 text-xs">{new Date(r.at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <ReferralLeaderboard className="mt-6" />
            <Link to="/parrains" className="mt-3 inline-block text-xs text-[#3ad7ff] hover:text-white" data-testid="referral-public-lb-link">{t("rl.public_link")}</Link>
          </>
        )}
      </div>
    </Page>
  );
}
